'use client';

import { buttonVariants } from '@/components/ui/button';
import { Dock, DockIcon } from '@/components/ui/dock';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { openWindow } from '@/redux/slice/windowSlice';
import { RootState } from '@/redux/store';
import { WindowData } from '@/utils/types/window';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { DockPosition, readDockIconPanels, readDockPinned, saveDockPinned } from '@/utils/dockPrefs';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Pin, PinOff } from 'lucide-react';
import Image from 'next/image';
import React, { memo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSwipeable } from 'react-swipeable';
import useMainMenuPermissions from '../permissions/useMainMenuFormPermissions';
const EMPTY_PINNED: Record<DockPosition, boolean> = {
  bottom: false,
  left: false,
  right: false
};
const MainMenu = () => {
  const dispatch = useDispatch();
  const windows = useSelector((state: RootState) => state.windows.windows);
  const user = useCurrentUser();
  const [popupVisible, setPopupVisible] = useState(false);
  const [popupWindows, setPopupWindows] = useState<WindowData[] | null>(null);
  const [activePopup, setActivePopup] = useState<string | null>(null);
  const [iconPanels, setIconPanels] = useState<Record<string, DockPosition>>({});
  const [panelPinned, setPanelPinned] = useState<Record<DockPosition, boolean>>(EMPTY_PINNED);
  const [panelVisible, setPanelVisible] = useState<Record<DockPosition, boolean>>(EMPTY_PINNED);

  // Load user-specific prefs once user ID is known
  useEffect(() => {
    if (!user?.id) return;
    const pinned = readDockPinned(user.id);
    setPanelPinned(pinned);
    setPanelVisible({
      bottom: pinned.bottom,
      left: pinned.left,
      right: pinned.right
    });
    setIconPanels(readDockIconPanels(user.id));
  }, [user?.id]);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const bottomDockRef = useRef<HTMLDivElement>(null);
  const leftDockRef = useRef<HTMLDivElement>(null);
  const rightDockRef = useRef<HTMLDivElement>(null);
  const leftScrollRef = useRef<HTMLDivElement>(null);
  const rightScrollRef = useRef<HTMLDivElement>(null);
  const bottomMobileScrollRef = useRef<HTMLDivElement>(null);
  const bottomDesktopScrollRef = useRef<HTMLDivElement>(null);
  type DockOrientation = 'vertical' | 'horizontal';
  const scrollToEdge = (ref: React.RefObject<HTMLDivElement>, edge: 'start' | 'end', orientation: DockOrientation = 'vertical') => {
    const el = ref.current;
    if (!el) return;
    if (orientation === 'vertical') {
      el.scrollTo({
        top: edge === 'start' ? 0 : el.scrollHeight,
        behavior: 'smooth'
      });
    } else {
      el.scrollTo({
        left: edge === 'start' ? 0 : el.scrollWidth,
        behavior: 'smooth'
      });
    }
  };

  // Refs prevent stale closures in the global mousedown handler
  const panelVisibleRef = useRef(panelVisible);
  const panelPinnedRef = useRef(panelPinned);
  useEffect(() => {
    panelVisibleRef.current = panelVisible;
  }, [panelVisible]);
  useEffect(() => {
    panelPinnedRef.current = panelPinned;
  }, [panelPinned]);
  const {
    menuItems,
    isLoading
  } = useMainMenuPermissions();
  const filteredMenuItems = Object.entries(menuItems);
  const handlers = useSwipeable({
    onSwipedLeft: () => setPanelVisible(prev => ({
      ...prev,
      bottom: false
    })),
    onSwipedRight: () => setPanelVisible(prev => ({
      ...prev,
      bottom: true
    }))
  });

  // Single combined click-outside handler — reads state via refs, no stale closures
  useEffect(() => {
    const refs: Record<DockPosition, React.RefObject<HTMLDivElement>> = {
      bottom: bottomDockRef,
      left: leftDockRef,
      right: rightDockRef
    };
    const handleGlobalMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (popupRef.current && !popupRef.current.contains(target)) {
        setPopupVisible(false);
      }
      (['bottom', 'left', 'right'] as DockPosition[]).forEach(pos => {
        if (panelVisibleRef.current[pos] && !panelPinnedRef.current[pos]) {
          if (!refs[pos].current?.contains(target)) {
            setPanelVisible(prev => ({
              ...prev,
              [pos]: false
            }));
          }
        }
      });
    };
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleGlobalMouseDown);
    }, 100);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleGlobalMouseDown);
    };
  }, []);
  useEffect(() => {
    if (popupVisible && activePopup !== null) {
      const menuWindows = windows.filter((w: WindowData) => w.title === activePopup);
      if (menuWindows.length > 0) setPopupWindows(menuWindows);
    }
  }, [windows]);
  useEffect(() => {
    setPopupWindows(windows);
  }, [windows]);

  // Sync icon panel assignments when QuickMenuSettings saves
  useEffect(() => {
    const handlePanelsChanged = () => {
      if (user?.id) setIconPanels(readDockIconPanels(user.id));
    };
    window.addEventListener('dockIconPanelsChanged', handlePanelsChanged);
    return () => window.removeEventListener('dockIconPanelsChanged', handlePanelsChanged);
  }, [user?.id]);
  const togglePin = (pos: DockPosition) => {
    if (!user?.id) return;
    const next = !panelPinned[pos];
    setPanelPinned(prev => ({
      ...prev,
      [pos]: next
    }));
    saveDockPinned(user.id, pos, next);
    if (!next) setPanelVisible(prev => ({
      ...prev,
      [pos]: false
    }));
  };
  const handleMenuClick = (e: React.MouseEvent, title: string, componentKey: string, pos: DockPosition) => {
    e.currentTarget.getBoundingClientRect();
    dispatch(openWindow({
      title,
      componentKey,
      id: null
    }));
    if (!panelPinned[pos]) setPanelVisible(prev => ({
      ...prev,
      [pos]: false
    }));
  };
  const handleContextMenu = (name: string, e: React.MouseEvent) => {
    e.preventDefault();
    const menuWindows = windows.filter((w: WindowData) => w.title === name);
    if (menuWindows.length > 0) {
      setActivePopup(name);
      setPopupWindows(menuWindows);
      setPopupVisible(true);
    }
  };
  const openPopupWindow = (title: string, componentKey: string, windowId: number, pos: DockPosition) => {
    dispatch(openWindow({
      title,
      componentKey,
      id: windowId
    }));
    setPopupVisible(false);
    if (!panelPinned[pos]) setPanelVisible(prev => ({
      ...prev,
      [pos]: false
    }));
  };

  // Popup opens away from the dock edge
  const popupPositionClass = (pos: DockPosition) => {
    if (pos === 'left') return 'left-full top-0 ml-2';
    if (pos === 'right') return 'right-full top-0 mr-2';
    return 'bottom-[66px] left-1/2 -translate-x-1/2';
  };
  const getItemsForPanel = (pos: DockPosition) => filteredMenuItems.filter(([, m]) => (iconPanels[m.module] ?? 'bottom') === pos);
  const tooltipSide = (pos: DockPosition): 'right' | 'left' | 'top' => {
    if (pos === 'left') return 'right';
    if (pos === 'right') return 'left';
    return 'top';
  };
  const renderMenuItems = (items: typeof filteredMenuItems, pos: DockPosition) => !isLoading && items.map(([name, mainmenu]) => {
    const IconComponent = mainmenu.icon;
    const win = windows.find((w: WindowData) => w.componentKey === mainmenu.module);
    const isWindowExist = !!win;
    return <DockIcon key={name}>
          <div className="relative">
            <Tooltip>
              <TooltipTrigger asChild>
                <button aria-label={mainmenu.name} className={cn(buttonVariants({
              variant: isWindowExist ? 'active' : 'bottomnav',
              size: 'icon'
            }), 'size-[24px] rounded-[6px] px-[3px] py-[3px] md:size-[32px] md:px-[5px] md:py-[5px] lg:size-11 lg:rounded-[8px] lg:px-2 lg:py-2')} onClick={e => handleMenuClick(e, isWindowExist ? win!.title : mainmenu.name, mainmenu.module, pos)} onContextMenu={e => handleContextMenu(mainmenu.name, e)}>
                  {IconComponent && <IconComponent />}
                </button>
              </TooltipTrigger>
              <TooltipContent side={tooltipSide(pos)} collisionPadding={12}>
                <p>{mainmenu.name}</p>
              </TooltipContent>
            </Tooltip>
          </div>
          {activePopup === mainmenu.name && popupVisible && popupWindows?.length ? <div ref={popupRef} className={cn('supports-backdrop-blur:bg-white/10 absolute z-50 flex max-h-[200px] flex-col gap-y-2 overflow-x-auto rounded-sm border-[1px] border-[#ddd] bg-[rgba(255,255,255,0.9)] px-4 py-3 shadow-lg backdrop-blur-md', popupPositionClass(pos))}>
              {popupWindows.map(visibleWindow => <button key={visibleWindow.id} onClick={() => openPopupWindow(visibleWindow.title, visibleWindow.componentKey, visibleWindow.id, pos)} className="flex min-w-[125px] flex-col items-center justify-center gap-y-1 rounded-[8px] bg-[#d5d5d578] px-3 py-2 text-xs font-semibold hover:bg-[#fff]">
                  <span className="text-xs text-[#2e2e2e]">{visibleWindow.title}</span>
                </button>)}
            </div> : null}
        </DockIcon>;
  });
  const renderControls = (pos: DockPosition) => <Tooltip data-sentry-element="Tooltip" data-sentry-component="renderControls" data-sentry-source-file="MainMenu.tsx">
      <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="MainMenu.tsx">
        <button aria-label={panelPinned[pos] ? 'Unpin dock' : 'Pin dock open'} className={cn(buttonVariants({
        variant: panelPinned[pos] ? 'active' : 'bottomnav',
        size: 'icon'
      }), 'size-[24px] rounded-[6px] px-[3px] py-[3px] md:size-[32px] md:px-[5px] md:py-[5px] lg:size-11 lg:rounded-[8px] lg:px-2 lg:py-2')} onClick={() => togglePin(pos)}>
          {panelPinned[pos] ? <PinOff className="size-4 lg:size-5" /> : <Pin className="size-4 lg:size-5" />}
        </button>
      </TooltipTrigger>
      <TooltipContent side={tooltipSide(pos)} collisionPadding={12} data-sentry-element="TooltipContent" data-sentry-source-file="MainMenu.tsx">
        <p>{panelPinned[pos] ? 'Unpin dock' : 'Pin dock open'}</p>
      </TooltipContent>
    </Tooltip>;
  const bottomItems = getItemsForPanel('bottom');
  const leftItems = getItemsForPanel('left');
  const rightItems = getItemsForPanel('right');
  const [leftScrollable, setLeftScrollable] = useState(false);
  const [rightScrollable, setRightScrollable] = useState(false);
  const [bottomMobileScrollable, setBottomMobileScrollable] = useState(false);
  const [bottomDesktopScrollable, setBottomDesktopScrollable] = useState(false);
  useEffect(() => {
    const check = () => {
      const el = leftScrollRef.current;
      setLeftScrollable(!!el && el.scrollHeight - el.clientHeight > 1);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [leftItems.length, panelVisible.left]);
  useEffect(() => {
    const check = () => {
      const el = rightScrollRef.current;
      setRightScrollable(!!el && el.scrollHeight - el.clientHeight > 1);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [rightItems.length, panelVisible.right]);
  useEffect(() => {
    const check = () => {
      const el = bottomMobileScrollRef.current;
      setBottomMobileScrollable(!!el && el.scrollHeight - el.clientHeight > 1);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [filteredMenuItems.length, panelVisible.bottom]);
  useEffect(() => {
    const check = () => {
      const el = bottomDesktopScrollRef.current;
      setBottomDesktopScrollable(!!el && el.scrollWidth - el.clientWidth > 1);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [bottomItems.length, panelVisible.bottom]);

  // Shared shell: start/end arrows (only when scrollable) + scroll area + a pin footer that
  // never scrolls. Used identically by all four dock instances (left, right, bottom mobile,
  // bottom desktop) so their spacing and behavior can't drift apart from one another.
  const renderDockShell = (orientation: DockOrientation, items: typeof filteredMenuItems, pos: DockPosition, scrollRef: React.RefObject<HTMLDivElement>, scrollable: boolean) => {
    const isVertical = orientation === 'vertical';
    const StartIcon = isVertical ? ChevronUp : ChevronLeft;
    const EndIcon = isVertical ? ChevronDown : ChevronRight;
    const arrowSizeClass = isVertical ? 'h-6 w-full' : 'h-full w-6';
    return <>
        {scrollable && <button type="button" aria-label={isVertical ? 'Scroll to top' : 'Scroll to start'} onClick={() => scrollToEdge(scrollRef, 'start', orientation)} className={cn('flex shrink-0 items-center justify-center bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-900', arrowSizeClass)}>
            <StartIcon className="h-5 w-5" />
          </button>}
        <div ref={scrollRef} className={cn('min-h-0 min-w-0 flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden', isVertical ? 'overflow-y-auto' : 'overflow-x-auto')}>
          <Dock direction="middle" className={cn('m-0 flex h-auto w-auto gap-1 rounded-none border-none bg-transparent p-2 shadow-none', isVertical ? 'flex-col' : 'flex-row')} data-sentry-element="Dock" data-sentry-source-file="MainMenu.tsx">
            {renderMenuItems(items, pos)}
          </Dock>
        </div>
        {scrollable && <button type="button" aria-label={isVertical ? 'Scroll to bottom' : 'Scroll to end'} onClick={() => scrollToEdge(scrollRef, 'end', orientation)} className={cn('flex shrink-0 items-center justify-center bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-900', arrowSizeClass)}>
            <EndIcon className="h-5 w-5" />
          </button>}
        {/* Pin stays put — outside the scroll area, always visible */}
        <div className={cn('flex shrink-0 items-center justify-center p-2', isVertical ? 'border-t border-t-gray-100' : 'border-l border-l-gray-100')}>
          {renderControls(pos)}
        </div>
      </>;
  };
  return <div className="select-none" data-sentry-component="MainMenu" data-sentry-source-file="MainMenu.tsx">
      <TooltipProvider data-sentry-element="TooltipProvider" data-sentry-source-file="MainMenu.tsx">
        {/* BOTTOM PANEL — mobile/tablet always shows ALL items in one left-side list (saved layout is desktop-only) */}
        <div {...handlers} ref={bottomDockRef} className="relative">
          {!panelVisible.bottom && <button className="fixed left-0 top-[64px] z-50 flex h-32 w-5 items-center justify-center rounded-r-xl bg-gray-500 opacity-75 focus:outline-none lg:hidden" onClick={() => setPanelVisible(prev => ({
          ...prev,
          bottom: true
        }))} aria-label="Show dock">
              <Image src="/images/next.png" alt="Show dock" width={18} height={18} />
            </button>}
          {/* Mobile/tablet — single combined list, ignores per-item panel assignment */}
          <div className={cn('fixed bottom-0 left-0 top-[64px] flex w-auto flex-col overflow-hidden rounded-[14px] border-[0.5px] border-solid border-[#FFFFFF33] bg-white backdrop-blur-md transition-transform duration-300 md:top-[100px] lg:hidden', panelVisible.bottom ? 'translate-x-0' : '-translate-x-full')}>
            {renderDockShell('vertical', filteredMenuItems, 'bottom', bottomMobileScrollRef, bottomMobileScrollable)}
          </div>

          {/* Desktop — honors the saved quick menu layout, only when items are assigned here */}
          {bottomItems.length > 0 && <>
              {!panelVisible.bottom && <button className="fixed bottom-0 left-1/2 hidden w-32 -translate-x-1/2 items-center justify-center rounded-t-[10px] bg-gray-500 opacity-75 focus:outline-none lg:flex" onClick={() => setPanelVisible(prev => ({
            ...prev,
            bottom: true
          }))} aria-label="Show dock">
                  <ChevronUp className="h-6 w-6 text-white" />
                </button>}
              <div className={cn('fixed bottom-0 left-1/2 z-40 hidden max-w-[calc(100vw-32px)] -translate-x-1/2 overflow-hidden rounded-[14px] border-[0.5px] border-solid border-[#FFFFFF33] bg-white backdrop-blur-md transition-transform duration-300 lg:flex', panelVisible.bottom ? 'translate-y-0' : 'translate-y-full')}>
                {renderDockShell('horizontal', bottomItems, 'bottom', bottomDesktopScrollRef, bottomDesktopScrollable)}
              </div>
            </>}
        </div>

        {/* LEFT PANEL — only renders when icons assigned */}
        {leftItems.length > 0 && <>
            {!panelVisible.left && <button className="fixed left-0 top-1/2 z-50 hidden h-32 w-5 -translate-y-1/2 items-center justify-center rounded-r-xl bg-gray-500 opacity-75 focus:outline-none lg:flex" onClick={() => setPanelVisible(prev => ({
          ...prev,
          left: true
        }))} aria-label="Show left dock">
                <ChevronRight className="h-4 w-4 text-white" />
              </button>}
            <div className={cn('fixed bottom-4 left-0 top-[80px] z-40 hidden items-center transition-transform duration-300 lg:flex', panelVisible.left ? 'translate-x-0' : '-translate-x-full')}>
              <div ref={leftDockRef} className="flex max-h-[calc(100vh-96px)] flex-col overflow-hidden rounded-[14px] border-[0.5px] border-solid border-[#FFFFFF33] bg-white backdrop-blur-md">
                {renderDockShell('vertical', leftItems, 'left', leftScrollRef, leftScrollable)}
              </div>
            </div>
          </>}

        {/* RIGHT PANEL — only renders when icons assigned */}
        {rightItems.length > 0 && <>
            {!panelVisible.right && <button className="fixed right-0 top-1/2 z-50 hidden h-32 w-5 -translate-y-1/2 items-center justify-center rounded-l-xl bg-gray-500 opacity-75 focus:outline-none lg:flex" onClick={() => setPanelVisible(prev => ({
          ...prev,
          right: true
        }))} aria-label="Show right dock">
                <ChevronLeft className="h-4 w-4 text-white" />
              </button>}
            <div className={cn('fixed bottom-4 right-0 top-[80px] z-40 hidden items-center transition-transform duration-300 lg:flex', panelVisible.right ? 'translate-x-0' : 'translate-x-full')}>
              <div ref={rightDockRef} className="flex max-h-[calc(100vh-96px)] flex-col overflow-hidden rounded-[14px] border-[0.5px] border-solid border-[#FFFFFF33] bg-white backdrop-blur-md">
                {renderDockShell('vertical', rightItems, 'right', rightScrollRef, rightScrollable)}
              </div>
            </div>
          </>}
      </TooltipProvider>
    </div>;
};
export default memo(MainMenu);